import type { S2PElevateSection, S2PElevateSlide } from "@/data/s2pElevateAI";

type SlideCopy = Partial<Pick<S2PElevateSlide, "title" | "lead" | "quote" | "prompt" | "note" | "bullets" | "questions">> & {
  sections?: Array<Partial<Pick<S2PElevateSection, "title" | "body">>>;
};

const outcomes = [
  "Reusable Skill: instructions you can use again.",
  "Selected Job: recurring work you can point to.",
  "Tool Decision: the simplest approved tool that can do the Job.",
  "Reviewed Agent Run: one controlled run with your decision recorded.",
  "Measurable Loop: a result you can use to improve the next run.",
];

// Spoken copy for /s2p-elevate-ai. Keep shared audience decks and source prompts intact.
const slideCopy: Record<string, SlideCopy> = {
  welcome: {
    lead: "Build your Chief of Staff one session at a time. Write the Skill, choose the Job and tool, review the first run, then use the evidence to improve it.",
  },
  "finish-line": {
    title: "Build your Chief of Staff in five sessions",
    bullets: outcomes,
  },
  "tool-to-team-thinking": {
    title: "Give AI a clear outcome and a way to check it",
    note: "You tell a Roomba to clean the floor. You set the boundaries and check the result. You don't direct every turn or vacuuming pattern.",
  },
  "three-layers": {
    sections: [
      { body: "Know what AI can do, where it breaks, and what's coming next." },
      { body: "Explain your role, goal, limits, preferences, and what you've already tried." },
      { body: "Choose the answer that fits your customer, culture, and situation." },
    ],
  },
  "prompt-multiplier": {
    lead: "Save the context and standards you'd otherwise have to explain each time.",
    sections: [
      { body: "Describe what a finished answer should look like." },
      { body: "Point to the files, systems, examples, or data you trust." },
      { body: "Say what can't change and which decisions stay with you." },
      { body: "Check accuracy, usefulness, rework, and time saved." },
    ],
  },
  "who-i-am-prompt": {
    title: "Give your Chief of Staff the context you use",
    lead: "Save your role, priorities, approved sources, output format, and review standard in a brief you can reuse in ChatGPT or Claude.",
    sections: [
      { title: "Save the method", body: "Keep the task, decision rules, examples, and review checklist together. Save a copy outside the product's memory settings." },
      { title: "Check your workspace", body: "Use your approved ChatGPT or Claude workspace. Ask its owner to confirm the features and data settings before adding work information." },
      { title: "Check what it received", body: "Ask which instructions and supplied sources ChatGPT or Claude is using. Fill any gaps before judging the result." },
    ],
  },
  "outcome-first-prompt-format": {
    lead: "Start with the result you want. Then name the work, sources, and checks it needs.",
    sections: [
      { body: "Invite the team to explore before settling on a solution." },
      { body: "Name the work: rewrite, compare, summarize, prioritize, design, or build." },
      { body: "Describe a result you can see and check." },
      { body: "Name the tradeoff you're unwilling to make." },
      { body: "Say what can't change, what needs approval, and the limits on scope, time, tone, or risk." },
      { body: "Tell the agent to use the material that follows rather than assume the answer." },
      { body: "Name the files, systems, examples, research, or data to use." },
    ],
  },
  "outcome-first-prompt-evidence": {
    note: "Make the request clear enough to hand off. VERB says what to do. OUTCOME says what done looks like. CONSTRAINTS set the limits. SOURCES say what to use.",
  },
  "get-unstuck": { title: "Reset when the answer misses" },
  "iterate-with-good-examples": {
    title: "Show the Skill what good looks like",
    lead: "Show it what to repeat next time, then correct the current draft.",
    note: "A reviewed example makes your standards easier to see and copy. Save what worked so you don't have to explain it again.",
  },
  "qa-1": {
    questions: [
      "The brief invents a deadline. Did you supply the real date, and did you check the answer against it?",
      "You've connected a calendar. How do you know the tool read its entries and the harness passed them to the model?",
      "The Skill says read only. Which software permission or control blocks a write?",
    ],
    note: "Explain what the model received and what the harness did. Why doesn't saving a Skill grant access? Use those answers to review your Skill.",
  },
  "session-3-job-first": {
    title: "Choose the work before the tool",
    sections: [
      { body: "Find the recurring trigger, sources, output, and review. Use that evidence to decide where AI could help." },
      { body: "Your Job tells you what the tool needs to access, produce, and leave for a person to decide." },
    ],
  },
  "session-3-jtbd-formula": { title: "Describe the progress you need" },
  "session-3-capture-candidates": {
    title: "Find three to five Jobs in your calendar",
    sections: [
      { body: "What needs to happen? Where did you see it? How often does it repeat?" },
      { body: "What starts the work? What should it produce? Who reviews it?" },
      { body: "How do you do it now? What would improve? What needs approval?" },
    ],
  },
  "rice-calibration-huddle": {
    title: "Check the assumption behind your first choice",
    sections: [
      { body: "Use frequency, value, feasibility, and risk to rank your Jobs." },
      { title: "Challenge one assumption", body: "Ask a partner which assumption they'd test or reject without more evidence." },
      { body: "Keep or change Job #1. Write down why it should get the first AI run." },
    ],
    note: "Be ready to explain your choice and change it when the facts change.",
  },
  "session-3": {
    lead: "Use your selected Job to choose the simplest approved tool, model, access, and authority that can do the work.",
  },
  "tool-selection-rubric": {
    title: "Four questions help you choose the tool",
    note: "Build in ChatGPT or Claude. Use these questions to check fit and missing access, then compare with how you do the work today. Record what's blocked if your approved setup can't do the Job.",
  },
  "connections-and-tools": {
    title: "Check what the tool can reach and do",
    sections: [
      {},
      { body: "List reading and drafting separately from changes, messages, publishing, and other external actions." },
      {},
    ],
    note: "Start with only the connection and authority you need for one useful run.",
  },
  "agent-access-ladder": {
    title: "Start with the access you need for one run",
  },
  "ai-setup-choices": {
    sections: [
      { body: "The trained engine. What can it reason about or create? Try it on your Job." },
      { body: "The software around the model. How does it handle context, tools, state, permissions, and stopping rules?" },
      { body: "Where you work and review: chat, a code editor (IDE), a file workspace, a browser, or an embedded service." },
    ],
  },
  "token-budget-controls": {
    title: "Spend your AI budget where it helps",
    sections: [
      { body: "Long prompts, stronger models, and full reruns use more of your allowance. Save that capacity for work that needs deeper judgment." },
      {},
      { body: "Keep the trigger and steps clear. Load large references or scripts when the Job needs them. Remove duplicate context." },
      { body: "Watch the first run. Correct it when it drifts, then save the fix in the Skill or GOOD_EXAMPLES." },
    ],
  },
  "ai-workflow-pilot": {
    lead: "Compare how you do the work today with the proposed ChatGPT or Claude workflow. Use the same task, approved evidence, and review checks.",
    sections: [
      { body: "Use the same inputs, permissions, Job, and review checks for both paths. Include missing information and failure cases." },
      { body: "Count quality, review and rework, elapsed time, retries, and total cost, including tool calls." },
      { body: "Name the owner and review date. Agree on minimum quality and acceptable effort before seeing the results. Use those standards to proceed, revise, or stop." },
    ],
    note: "Agree on the comparison in today's closing discussion. Test it under supervision in Session 4, then review the evidence in Session 5. Choose a sample size and thresholds that fit your work.",
  },
  "showcase-debrief": {
    title: "Why did you choose this tool?",
    note: "Keep building your Chief of Staff. Use the AI Watcher to compare a second kind of Job.",
  },
  "session-4-build-output-review": {
    note: "An interface makes the output easier to use. To run it as a recurring agent, you still need a trigger, approved sources, controls, and a responsible owner. Save one correction for Session 5.",
  },
  "session-5-metric-loop-why": {
    title: "Measure whether the work got better",
  },
  "session-5-metric-loop-pick-metric": {
    title: "Choose one result to track and improve",
    lead: "Pick one metric tied to your Job. Use these Chief of Staff and AI Watcher examples to make it specific.",
    sections: [{}, {}, { title: "Changes that need a decision" }],
  },
  "session-5-metric-loop-brief": {
    title: "Define the metric before you compare runs",
  },
  "session-5-metric-loop-source": {
    title: "Check the number before you ask AI to track it",
  },
  "session-5-metric-loop-prompt": {
    prompt: `How might we compare [METRIC] with [BASELINE] in ChatGPT or Claude, within draft-only work and a consistent metric definition, based on [APPROVED SOURCE OR SAMPLE SNAPSHOT]?

Use [METRIC DEFINITION]. Check the period and filters. Show the current value, change from baseline, sources, and gaps. Separate what you observed from possible explanations. Suggest one instruction change and compare it with the same inputs and review checks.

Draft the update for [OWNER] to review for [DESTINATION]. Do not send, schedule, change records, or take external action. Propose [PROPOSED CADENCE] for review. Mark missing measurements unknown.`,
  },
  "session-5-metric-loop-support-example": {
    prompt: `How might we prepare a sourced follow-up queue in ChatGPT or Claude, within human review and no messages or record changes, based on [APPROVED MEETING NOTES AND COMMITMENT TRACKER] at [REVIEW DATE]?

Use only these sources. List commitments without a completed next step. Report the count and source IDs using the agreed definition. Flag missing owners and conflicting dates; do not invent either. Draft the follow-up queue for [REVIEWER]. Do not send messages, schedule a run, or change records.

For today's comparison, change one instruction and rerun the SAME source snapshot. Compare quality and review effort. Use new periods later to check the business outcome. Don't claim this manual test reduced real unresolved commitments.`,
  },
  "session-5-token-budget-prompt": {
    lead: "Set a budget and keep the quality checks. Compare runs on the same task and review standard.",
    sections: [
      { body: "Say whether X means tokens or dollars. For dollars, confirm the model rates and which charges count." },
      { body: "Use an available usage or budget tracker. Record what you've spent and what's left. Say when either number is unavailable." },
      { body: "Save enough budget to check the result and hand it off. If the task won't fit, report the gap before spending more." },
    ],
  },
  "feedback-and-schedule": {
    title: "Use the review to improve the next run",
    sections: [
      {},
      { body: "Compare the metric and evidence with your baseline and definition of done." },
      { body: "Choose one change within the approved scope. Keep consequential decisions with the responsible person." },
      { body: "Save the correction or reviewed example. Use the same measure to compare the next run." },
    ],
  },
  "session-5-metric-loop-build": {
    sections: [
      { body: "Reuse the approved source or sample snapshot and Session 4 output. Check the metric definition, source values, and review standard." },
      {},
      {},
      { body: "Check sources, invented values, and issue flags. Record review time and corrections for both results. Mark unavailable measurements unknown." },
      {},
    ],
  },
  "session-5-metric-loop-done": {
    title: "Show what improved and what still needs work",
    quote: "Use what you learned in the next run.",
    note: "Compare ChatGPT or Claude with how you do the work today. Check accepted quality, review effort, reliability, and cost. Record proceed, revise, or stop. Write down any source, permission, or ownership gaps before your next decision. A good demo still needs approval before production use.",
  },
  complete: {
    lead: "Save your instructions, required sources, review notes, and next improvement. Use your reviewed results to decide whether to keep going in ChatGPT or Claude.",
    bullets: outcomes,
  },
};

export const applyElevateCoreVoice = (slide: S2PElevateSlide): S2PElevateSlide => {
  const copy = slideCopy[slide.id];
  if (!copy) return slide;
  return {
    ...slide,
    ...copy,
    sections: slide.sections?.map((section, index) => ({ ...section, ...copy.sections?.[index] })),
  };
};
